///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    AfterViewInit,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { filter } from 'rxjs/operators';
import { GtthEntityInfoWindowContent } from '../gtth-entity-info-window-content-interface';
import { GtthStepGroupEnum, ProductTypeEnum } from '../../../../generated/shared-types.model';
import { GtthHistoryService } from '../../engine/history-service';
import { LinkFeature } from '../../entities/link/link-feature';
import { GtthBomReportService } from '../../../project-windows/bom_report/bom-report.service';
import { TopologyLinkTypeEnum } from '../../../../services/project-socket/dto/topology-types.model';
import { ProjectProductsService } from '../../engine/project-products-service';
import { GtthProjectInfoViewService } from '../../../../layouts/navbar/project-info-view.service';
import { GtthInfoWindowsService } from '../info-windows-service';
import { LinkProduct } from '../../entities/link/link-product';
import { FormControl, NgForm, NgModel } from '@angular/forms';
import { GtthProjectStateService } from '../../engine/project-state.service';
import { LinkInfoStateService } from './link-info-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'winde-link-info',
    templateUrl: './link-info.component.html',
    styleUrls: ['../entities-info-window.scss'],
    providers: [LinkInfoStateService],
    standalone: false
})
export class GtthLinkInfoComponent extends GtthEntityInfoWindowContent implements OnInit, AfterViewInit {
    public static readonly CUSTOM_ELEMENT_TAG_NAME = 'gtth-link-info-custom-tag-element';

    @ViewChild(NgForm) ngForm: NgForm;
    @ViewChildren(NgModel) ngModelList: QueryList<NgModel>;

    forceClose = false;

    public stepGroupEnum = GtthStepGroupEnum;
    public linkTypeEnum = TopologyLinkTypeEnum;

    @Output()
    public closeCallback = new EventEmitter<void>();

    public link: LinkFeature;
    public linkLayerName: string;
    public distance: number;
    public linkProducts: LinkProduct[];
    public devicesSupportAnIpConfiguration = false;

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly bomReportService: GtthBomReportService,
        private readonly projectInfoService: GtthProjectInfoViewService,
        private readonly projectProductsService: ProjectProductsService,
        public readonly projectStateService: GtthProjectStateService,
        public readonly historyService: GtthHistoryService,
        public readonly linkInfoStateService: LinkInfoStateService,
        infoWindowService: GtthInfoWindowsService
    ) {
        super();
        infoWindowService.registerActiveInfoWindow(this);
    }

    ngOnInit() {
        this.linkInfoStateService
            .subscribeToCloseEvent()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.closeCallback.next());

        this.initLinkData();

        if (this.historyService.interactiveWindowsService.bomReviewIsOpen() && this.link) {
            this.bomReportService
                .subscribeToLinksChanges()
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(
                        (linkNodeNames: string[]) =>
                            linkNodeNames[0] === this.link.getFromStation().getName() &&
                            linkNodeNames[1] === this.link.getToStation().getName()
                    )
                )
                .subscribe(() => {
                    this.initLinkData();
                });
        }
    }

    ngAfterViewInit() {
        this.ngForm.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            setTimeout(() => {
                this.ngModelList.forEach((ngModel: NgModel) => {
                    ngModel.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                });
            });
        });
    }

    initLinkData(): void {
        this.link = this.historyService.logicalTopology.getLink(this.entityId);

        if (this.historyService.logicalTopology.layersModeEnabled) {
            this.linkLayerName = this.projectInfoService.getLayersAsKeyValueObj()?.[this.link.getLinkLayerIndex()];
        }

        this.linkInfoStateService.setLink(this.link);
        this.distance = Math.round(this.link.getDistance());

        if (this.link.getLinkType() === TopologyLinkTypeEnum.LINK) {
            this.devicesSupportAnIpConfiguration =
                this.projectProductsService.getBaseRadioById(this.link.getBuUsedProduct()).productType === ProductTypeEnum.TG_MH;
            this.linkProducts = this.bomReportService.getLinkProducts(this.link);
        }
    }

    isValid(): boolean {
        return true;
    }

    changeLinkAn(linkProduct: LinkProduct, newAssignedName: string, formControl: FormControl) {
        formControl.updateValueAndValidity();

        if (formControl.errors?.error) {
            return;
        }

        linkProduct.device.getNeighboursNames().forEach((neighbourName: string) => {
            const nodeName = linkProduct.node.getName();
            const relatedLink = this.historyService.logicalTopology.getLinkByNodeNames(nodeName, neighbourName);
            const modifyCommand = this.linkInfoStateService.getModifyLinkNodesDataCommand();
            modifyCommand.addLinkToCommand(relatedLink);

            if (nodeName === relatedLink.getFromStation().getName()) {
                modifyCommand.setNewAssignedNamesToLink(relatedLink, newAssignedName, undefined);
            } else {
                modifyCommand.setNewAssignedNamesToLink(relatedLink, undefined, newAssignedName);
            }
        });
    }

    changeLinkIp(linkProduct: LinkProduct, newIpAddress: string, formControl: FormControl) {
        formControl.updateValueAndValidity();

        if (formControl.errors?.error) {
            return;
        }

        linkProduct.device.getNeighboursNames().forEach((neighbourName: string) => {
            const nodeName = linkProduct.node.getName();
            const relatedLink = this.historyService.logicalTopology.getLinkByNodeNames(nodeName, neighbourName);
            const modifyCommand = this.linkInfoStateService.getModifyLinkNodesDataCommand();
            modifyCommand.addLinkToCommand(relatedLink);

            if (nodeName === relatedLink.getFromStation().getName()) {
                modifyCommand.setNewIpsToLink(relatedLink, newIpAddress, undefined);
            } else {
                modifyCommand.setNewIpsToLink(relatedLink, undefined, newIpAddress);
            }
        });
    }
}
